import {AuthService} from '../../services/auth.service';
import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, Observable, from, throwError} from 'rxjs';
import {Router} from '@angular/router';
import {catchError, filter, map, switchMap, take} from 'rxjs/operators';
import {StorageService} from '../../services/storage-service.service';
import {environment} from '../../../environments/environment';

@Injectable()
export class HttpRequestInterceptor implements HttpRequestInterceptor {

  // Guards against every in-flight request firing its own refresh call the
  // moment the access token expires — the first 401 triggers the refresh,
  // everyone else queues on refreshTokenSubject and retries once it lands.
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private router: Router,
    private auth: AuthService,
    private storageService: StorageService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const currentClient = this.storageService.get('currentClient');
    const tempClient = localStorage.getItem('tempClient');

    if (!environment.production) {
      console.log('Access Token', currentClient);
      // console.log('refresh Token', this.storageService.get('refreshToken'));

    }

    if (currentClient) {
      // console.log("here")
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${currentClient}`
        }
      });
      // request = request.clone({url: request.url + '?access_token=' + currentClient});
    }

    const passwordResetRegex: RegExp = /^\/password-reset\/[a-zA-Z0-9]+$/;
    if (tempClient && (this.router.url === '/' || this.router.url === '/landing' ||
      this.router.url === '/login' || passwordResetRegex.test(this.router.url))
    ) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${tempClient}`
        }
      });
    }

    // The chat endpoint is a secondary AI feature (240s timeout budget,
    // prone to network hiccups/slow responses) — a single failed request
    // there shouldn't wipe the user's whole POS session. Let the chat
    // widget's own catch block show its own inline error instead.
    const isChatRequest = request.url.includes('/store/chat/');
    // Never attempt to "refresh" the refresh call itself — a 401 here
    // means the refresh token is genuinely dead, not that it needs renewing.
    const isRefreshRequest = request.url.includes('/auth/jwt/refresh/');

    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error?.status === 401 && !isRefreshRequest && this.storageService.get('refreshToken')) {
          return this.handleUnauthorized(request, next, isChatRequest);
        }

        if (isChatRequest) {
          return throwError(error);
        }

        if (error?.statusText === 'Unknown Error' && error?.status === 0){
          // A network/connectivity failure isn't evidence the session is
          // invalid — let the user know, but don't log them out over it.
          this.auth.openSnackbar('Connection Lost, Check your Internet Connection or Contact Support Team for assistance');
        }else {
          const data = {
            reason: error && error?.error?.error_description ? error?.error?.error_description : '',
            status: error.status,
            causedBy: error.message
          };
          this.auth.collectFailedRequest(data);
        }
        return throwError(error);
      }));
  }

  private handleUnauthorized(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    isChatRequest: boolean
  ): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return from(this.auth.refreshAccessToken()).pipe(
        switchMap((newToken: string) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.withToken(request, newToken));
        }),
        catchError((refreshError) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);

          // The refresh token itself is invalid/expired — the session is
          // genuinely over. Chat still gets to show its own inline error
          // instead of yanking the user away mid-conversation, though.
          if (!isChatRequest) {
            this.auth.clearStore();
            this.auth.openSnackbar('Your session has expired. Please log in again.');
          }
          return throwError(refreshError);
        })
      );
    }

    // A refresh is already in flight for another request — wait for it
    // to land, then retry this one with the token it produced.
    return this.refreshTokenSubject.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap((token) => next.handle(this.withToken(request, token)))
    );
  }

  private withToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }

}
